import { useParams } from 'react-router-dom';

import { useAppSelector } from '@/redux/hooks';
import { allGeoAreasSelector, createGeoAreaByIdSelector } from '@/redux/modules/geoArea/geoArea.selectors';
import { GeoArea } from '@/types/GeoArea';

import { findGeoAreaFromUrlParams } from '../helpers/geoAreaToUrlParams';
import { AuctionsNearMeRouteParams } from '../types/AuctionsNearMe.types';

type UseGeoAreaFromUrlReturn = {
    geoArea: GeoArea | undefined;
    hasNewUrlParams: boolean;
    hasOldUrlParams: boolean;
    routeParams: AuctionsNearMeRouteParams;
};

export const useGeoAreaFromUrl = (): UseGeoAreaFromUrlReturn => {
    const routeParams = useParams<AuctionsNearMeRouteParams>();
    const allGeoAreas = useAppSelector(allGeoAreasSelector);

    // Derive URL format flags
    const hasNewUrlParams = Boolean(
        ('city' in routeParams && routeParams.city) ||
        ('country' in routeParams && routeParams.country) ||
        ('state' in routeParams && routeParams.state)
    );
    const hasOldUrlParams = Boolean('geoAreaId' in routeParams && routeParams.geoAreaId);

    // Resolve geoArea from URL params
    const geoAreaIdFromUrl = hasOldUrlParams && 'geoAreaId' in routeParams ? Number(routeParams.geoAreaId) : 0;
    const geoAreaFromOldUrl = useAppSelector(createGeoAreaByIdSelector(geoAreaIdFromUrl));
    const geoAreaFromNewUrl = hasNewUrlParams
        ? findGeoAreaFromUrlParams(allGeoAreas, {
              city: 'city' in routeParams ? routeParams.city : undefined,
              country: 'country' in routeParams ? routeParams.country : undefined,
              state: 'state' in routeParams ? routeParams.state : undefined,
          })
        : undefined;
    const geoArea = hasNewUrlParams ? geoAreaFromNewUrl : geoAreaFromOldUrl;

    return {
        geoArea,
        hasNewUrlParams,
        hasOldUrlParams,
        routeParams,
    };
};
